import React, { useState } from "react";
import DatePicker from "react-datepicker";
import { Controller } from "react-hook-form";
import styled from "styled-components";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import ValidationErrorText from "./ValidationErrorText";
import { setHours, setMinutes } from "date-fns";

const TimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 20px;
  .error-adjustment {
    padding-left: ${({ lang }) => (lang === "ar" ? "0" : "190px")};
    padding-right: ${({ lang }) => (lang === "ar" ? "190px" : "0")};
    @media (max-width: 768px) {
      padding-left: ${({ lang }) => (lang === "ar" ? "0" : "30px")};
      padding-right: ${({ lang }) => (lang === "ar" ? "30px" : "0")};
    }
  }
`;

const Wrapper = styled.div`
  margin-bottom: 0.5rem;
  position: relative;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: ${({ lang }) => (lang === "ar" ? "0 18px 0 14px" : "0 24px")};
  margin-right: ${({ lang }) => (lang === "ar" ? "12px" : "")};
  /* gap: ${({ lang }) => (lang === "ar" ? "5.8rem" : "5.8rem")}; */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: start;
  }

  /* @media (max-height: 768px) {
    gap: 105px;
  } */

  label {
    font-weight: 500;
    font-size: 14px;
    color: #525355;
    margin-bottom: 8px;
    display: block;
  }

  .datepicker-wrapper {
    display: flex;
    align-items: center;
    position: relative;
    width: 100%;
  }

  .time-label {
    font-size: 14px;
    margin-right: 8px;
    color: var(--text);
    margin-left: 20px;
    @media (max-width: 768px) {
      margin-left: 0;
    }
  }

  .custom-time-picker {
    width: 100%;
    padding: ${({ $isRtl }) =>
      $isRtl ? "12px 16px 12px 40px" : "12px 40px 12px 16px"};
    font-size: 14px;
    border: 1px solid var(--borderLight);
    border-radius: 10px;
    outline: none;
    background-color: ${({ bg }) => (bg ? bg : "var(--white)")};
    color: var(--text);
    position: relative;
    display: block;

    &::placeholder {
      color: #999;
    }
  }

  .icon-container {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 16px;
    pointer-events: none;

    &.ltr {
      right: 10px;
    }

    &.rtl {
      left: 10px;
    }
  }

  .error {
    color: #e53935;
    font-size: 12px;
    margin-top: 4px;
  }
  .react-datepicker__current-month,
  .react-datepicker-time__header,
  .react-datepicker-year-header {
    color: #525355;
  }

  .react-datepicker {
    border-radius: 10px;
    border: 1px solid var(--borderLight);
    background-color: ${({ bg }) => (bg ? bg : "var(--white)")};
    padding: 8px;
    color: var(--text);
    width: 100%;
  }
  .react-datepicker__header {
    color: #525355 !important;
  }

  .react-datepicker__time-container {
    border-left: none;
  }

  .react-datepicker__time {
    font-family: inherit;
  }

  .react-datepicker__time-box {
    width: 100px;
  }

  .react-datepicker-wrapper {
    width: 100%;
    display: block;
  }

  .react-datepicker__time-list-item {
    padding: 12px;
    border-radius: 6px;
    font-size: 14px;

    &:hover {
      background-color: var(--iconColor) !important;
      color: #fff !important;
    }

    &--selected {
      background-color: #009afe !important;
      color: white;
      font-weight: bold;
      padding: 5px 2px !important;
    }
  }
`;

const TimeOnlyPicker = ({
  control,
  errors,
  name,
  label,
  icon: Icon,
  bg,
  minTime,
  maxTime,
  isDisabledFrom,
  isDisabledTill,
  filterTimeFrom,
  filterTimeTill,
  disabled,
  rules = {},
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const directionClass = isRtl ? "rtl" : "ltr";

  const [startDate, setStartDate] = useState();

  // const filterTime = (time) => {
  //   if (!fromDate) return false;
  //   const now = new Date();
  //   const selectedDate = new Date(fromDate);
  //   const isToday =
  //     selectedDate.getDate() === now.getDate() &&
  //     selectedDate.getMonth() === now.getMonth() &&
  //     selectedDate.getFullYear() === now.getFullYear();

  //   if (isToday) {
  //     return time.getTime() >= now.getTime();
  //   }

  //   return true;
  // };

  // const isDisabled = !fromDate;

  return (
    <TimeContainer lang={i18n.language}>
      <Wrapper bg={bg} $isRtl={isRtl} lang={i18n.language}>
        <p className="time-label" style={{ width: "153px" }}>
          {t("time")}
        </p>
        <div className="datepicker-wrapper">
          <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field }) => (
              <>
                <DatePicker
                  {...field}
                  value={field?.value}
                  selected={field?.value}
                  onChange={(date) => field.onChange(date)}
                  showTimeSelect
                  showTimeSelectOnly
                  showSecondHand
                  timeIntervals={1}
                  timeCaption="Time"
                  dateFormat="HH:mm:ss"
                  placeholderText={t("select_time")}
                  className="custom-time-picker"
                  filterTime={filterTimeFrom ? filterTimeFrom : filterTimeTill}
                  // disabled={isDisabledFrom ? isDisabledFrom : isDisabledTill}
                  disabled={disabled}
                  minTime={minTime && minTime}
                  maxTime={maxTime && maxTime}
                  onChangeRaw={(e) => {
                    const allowed = /^[0-9:/\-]*$/;
                    if (!allowed.test(e.target.value)) {
                      e.preventDefault();
                    }
                  }}
                />
                {Icon && (
                  <Icon
                    className={`icon-container ${directionClass}`}
                    width="19"
                    height="19"
                  />
                )}
              </>
            )}
          />
        </div>
      </Wrapper>
      {errors?.[name] && (
        <ValidationErrorText
          extraClass="error-adjustment"
          text={t("fieldRequired")}
        />
      )}
    </TimeContainer>
  );
};

export default TimeOnlyPicker;
